import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  MenuItem,
  TextField,
  IconButton,
  Tooltip,
  Button,
  Stack,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import {useThunk} from "../hooks/useThunk";
import {fetchHonorariums} from "../store";
import {ROLES} from "../utils/constants";
import AddHonorariumDialog from "../components/honorarium/AddHonorariumDialog";

const CAN_CREATE_HONORARIUM_ROLES = [
  ROLES.ACCOUNTANT,
  ROLES.MANAGING_DIRECTOR,
  ROLES.SUPER_ADMIN,
];

const HonorariumManagement = () => {
  const {honorariums, summary, pagination, loading, error} = useSelector(
    (state) => state.honorarium
  );
  const {userData} = useSelector((state) => state.auth);
  const canCreate = CAN_CREATE_HONORARIUM_ROLES.includes(userData?.role);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const navigate = useNavigate();

  const [doFetchHonorariums] = useThunk(fetchHonorariums);

  const refetch = () =>
    doFetchHonorariums({
      month: selectedMonth,
      year: selectedYear,
      page: page + 1,
      limit: rowsPerPage,
    });

  const months = [
    {value: 1, label: "January"},
    {value: 2, label: "February"},
    {value: 3, label: "March"},
    {value: 4, label: "April"},
    {value: 5, label: "May"},
    {value: 6, label: "June"},
    {value: 7, label: "July"},
    {value: 8, label: "August"},
    {value: 9, label: "September"},
    {value: 10, label: "October"},
    {value: 11, label: "November"},
    {value: 12, label: "December"},
  ];

  const years = Array.from(
    {length: 5},
    (_, i) => new Date().getFullYear() - 2 + i
  );

  useEffect(() => {
    doFetchHonorariums({
      month: selectedMonth,
      year: selectedYear,
      page: page + 1,
      limit: rowsPerPage,
    });
  }, [doFetchHonorariums, page, rowsPerPage, selectedMonth, selectedYear]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMonthChange = (event) => {
    setSelectedMonth(event.target.value);
    setPage(0);
  };

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
    setPage(0);
  };

  const handleViewDetails = (honorariumId) => {
    navigate(`/dashboard/honorarium/${honorariumId}`);
  };

  if (loading && !honorariums.length) {
    return (
      <Box sx={{display: "flex", justifyContent: "center", mt: 4}}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{p: 2}}>
      <Stack
        direction={{xs: "column", sm: "row"}}
        justifyContent="space-between"
        alignItems={{xs: "stretch", sm: "center"}}
        spacing={2}
        sx={{mb: 3}}>
        <Typography variant="h4">Honorarium Management</Typography>
        {canCreate && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            sx={{textTransform: "none"}}>
            Add Honorarium
          </Button>
        )}
      </Stack>
      <AddHonorariumDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onCreated={refetch}
        defaultMonth={selectedMonth}
        defaultYear={selectedYear}
      />

      <Card sx={{mb: 3}}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Month"
                value={selectedMonth}
                onChange={handleMonthChange}>
                {months.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Year"
                value={selectedYear}
                onChange={handleYearChange}>
                {years.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{mb: 2}}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell align="right">Present Days</TableCell>
              <TableCell align="right">Absent Days</TableCell>
              <TableCell align="right">Net Honorarium</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Admin Approved</TableCell>
              <TableCell>Remarks</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {honorariums?.map((honorarium) => (
              <TableRow key={honorarium._id}>
                <TableCell>
                  {honorarium.userId.firstName} {honorarium.userId.lastName}
                </TableCell>
                <TableCell align="right">
                  {honorarium.totalPresentDays}
                </TableCell>
                <TableCell align="right">
                  {honorarium.totalAbsentDays}
                </TableCell>
                <TableCell align="right">₹{honorarium.netHonorarium}</TableCell>
                <TableCell>{honorarium.paymentStatus}</TableCell>
                <TableCell>{honorarium.adminApproved}</TableCell>
                <TableCell>{honorarium.remarks[0]}</TableCell>
                <TableCell align="center">
                  <Tooltip title="View Details">
                    <IconButton
                      color="primary"
                      onClick={() => handleViewDetails(honorarium._id)}>
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={pagination.total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <Card sx={{mt: 3}}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle1">Total Records:</Typography>
              <Typography variant="h6">{summary?.totalRecords}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle1">Total Net Amount:</Typography>
              <Typography variant="h6">₹{summary?.totalNetAmount}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default HonorariumManagement;
